import { Suspense, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { FleetConfigTable } from "./FleetConfigTable";
import { useLEZ, usePredefinedLEZOptions } from "src/hooks";
import type { Scenario } from "src/types";
import { useScenario } from "src/hooks/useScenario";
import { isDisabled } from "src/utils/permission.utils";
import { ErrorBoundary } from "react-error-boundary";
import { PredefinedFleetConfigSelection } from "src/components/PredefinedFleetConfigSelection";

interface Props {
  scenarioId: number | string;
  lezId: number | string;
  open: boolean;
  onClose: () => void;
}

export function FleetConfigDialog(props: Props) {
  const { data: scenario } = useScenario(props.scenarioId);

  const { t } = useTranslation();

  return render();

  function render() {
    if (!scenario) return null;

    return (
      <StyledDialog fullWidth maxWidth="md" open={props.open}>
        <DialogContent>
          <Typography variant="h2" gutterBottom>
            {t("label.vehicleRestrictions")}
          </Typography>
          <Box mb={1}>
            <Typography>{t("label.vehicleRestrictionsInstructions")}</Typography>
          </Box>
          <ErrorBoundary fallback={null}>
            <Suspense fallback={<CircularProgress />}>
              <LoadData {...props} scenario={scenario} />
            </Suspense>
          </ErrorBoundary>
        </DialogContent>
      </StyledDialog>
    );
  }
}

function LoadData(props: Props & { scenario: Scenario }) {
  const { scenario, lezId, onClose } = props;

  const { data: predefinedLEZs } = usePredefinedLEZOptions();
  const { data: lez } = useLEZ(scenario.id, lezId);

  const [selectedFleetOption, setSelectedFleetOption] = useState<"custom" | "predefined">(
    lez?.predefinedFleet ? "predefined" : "custom"
  );

  return (
    <>
      <FormControl>
        <RadioGroup
          value={selectedFleetOption}
          onChange={(e) => setSelectedFleetOption(e.target.value as "custom" | "predefined")}
        >
          <FormControlLabel value="custom" control={<Radio size="small" />} label="Define custom restrictions" />
          <FormControlLabel
            value="predefined"
            control={<Radio size="small" disabled={predefinedLEZs?.length === 0} />}
            label="Use predefined fleet"
          />
        </RadioGroup>
      </FormControl>
      {selectedFleetOption === "predefined" && (
        <PredefinedFleetConfigSelection
          defaultValue={lez?.predefinedFleetNameKey || ""}
          options={predefinedLEZs || []}
          readOnly={isDisabled(scenario.permissions.updateLEZ)}
          lezId={lezId}
          scenarioId={scenario.id}
          onClose={onClose}
        />
      )}

      {selectedFleetOption === "custom" && (
        <Suspense fallback={<CircularProgress />}>
          <FleetConfigTable
            className="fleetConfig"
            lezId={lezId}
            scenarioId={scenario.id}
            readOnly={isDisabled(scenario.permissions.updateLEZ)}
            onClose={onClose}
          />
        </Suspense>
      )}
    </>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialogActions-root {
    margin-top: auto;
    padding: ${({ theme }) => theme.spacing(3)};
  }
  .MuiCircularProgress-root {
    margin: auto;
  }
  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100vh;
    max-height: 900px;
  }

  .MuiRadio-root {
    padding: 4px 4px;
  }
  .MuiFormControl-root {
    margin-bottom: 16px;
    margin-left: 4px;
  }
`;
